import React, { useMemo } from 'react';
import { Paper, Box, Typography, Button, Chip, Stack } from '@mui/material';
import { Inbox as InboxIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { skipWhenOffline } from '../../offline';
import { inboxService } from '../../services/inboxService';
import { UnallocatedItem } from '../../types/inbox';

/**
 * Compact summary card for unallocated lab/imaging results that need triage.
 * Mounted on the Patients page, for people who may see lab results, so
 * clinicians see it before they pick a patient.
 *
 * Hidden entirely when there are no pending items (no visual noise on the
 * happy path).
 */
const UnallocatedResultsCard: React.FC = () => {
  const navigate = useNavigate();
  const { data: items = [] } = useQuery<UnallocatedItem[]>(
    ['inbox', 'PENDING'],
    () => inboxService.list('PENDING'),
    { enabled: skipWhenOffline(), refetchOnWindowFocus: true, refetchInterval: 60_000 },
  );

  const pending = useMemo(() => items.filter(i => i.triageStatus === 'PENDING'), [items]);
  const sources = useMemo(() => {
    const set = new Set(pending.map(i => i.source));
    return Array.from(set);
  }, [pending]);

  if (pending.length === 0) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, sm: 2 },
        mb: { xs: 2, sm: 3 },
        borderRadius: 3,
        borderColor: 'warning.main',
        bgcolor: 'warning.50',
        boxShadow: 'none',
      }}
    >
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
          <InboxIcon sx={{ color: 'warning.main', fontSize: 28, flexShrink: 0 }} />
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={600}>
                {pending.length} unallocated {pending.length === 1 ? 'result' : 'results'}
              </Typography>
              <Chip label="Needs triage" size="small" color="warning" />
            </Box>
            <Typography variant="caption" color="text.secondary" component="div">
              From {sources.join(', ')} — assign to the right patient or reject.
            </Typography>
            {/* Show 3 most recent as a teaser */}
            <Stack direction="row" spacing={1} mt={0.75} flexWrap="wrap" useFlexGap>
              {pending.slice(0, 3).map(p => (
                <Typography key={p.id} variant="caption" color="text.secondary">
                  · {p.kind === 'imaging_study' ? p.modality ?? 'Imaging' : p.panelName ?? 'Lab'}
                  {p.patientNameOnReport ? ` — "${p.patientNameOnReport}"` : ''}
                </Typography>
              ))}
              {pending.length > 3 && (
                <Typography variant="caption" color="text.secondary">
                  · +{pending.length - 3} more
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="warning"
          endIcon={<ChevronRightIcon />}
          onClick={() => navigate('/clinical-records/unallocated')}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          Review
        </Button>
      </Box>
    </Paper>
  );
};

export default UnallocatedResultsCard;
